import { usePostHog } from "posthog-js/react";
import { type ReactNode, useCallback, useMemo } from "react";

import type { AdminTicketTabsStatsDto, PortfolioAdminTicketDto, PortfolioMinimalTicketDto } from "@/api/types";
import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { ContextMenu, type ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { ClockIcon } from "@/components/Icons/ClockIcon";
import { FilterFunnel01Icon } from "@/components/Icons/FilterFunnel01Icon";
import { MessageCircle01Icon } from "@/components/Icons/MessageCircle01Icon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import type { ModalState } from "@/components/Modal/Modal";
import { useModalState } from "@/components/Modal/Modal";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { SearchInput } from "@/components/SearchInput/SearchInput";
import { getRemsStatus } from "@/components/Ticket/helpers";
import { RemsStatus } from "@/components/Ticket/RemsStatus";
import { TicketCard } from "@/components/Ticket/TicketCard";
import { TicketStatus } from "@/components/Ticket/TicketStatus";
import { TicketTableActions } from "@/components/Ticket/TicketTableActions";
import { TicketVisibilityIndicator } from "@/components/Ticket/TicketVisibilityIndicator";
import { User, UserTooltipContent } from "@/components/User/User";
import { UserAvatar } from "@/components/UserAvatar/UserAvatar";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { Inline as LayoutInline } from "@/design-system/Inline";
import { NumericBadge } from "@/design-system/NumericBadge";
import { RatingBadge } from "@/design-system/RatingBadge";
import { Spinner } from "@/design-system/Spinner";
import { Stack } from "@/design-system/Stack";
import {
  createDashboardColumnHelper,
  type DashboardRow,
  type DashboardTableState,
  TABLE_COLUMN_SIZES,
} from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import type { TabItem } from "@/design-system/Tabs";
import { Tabs } from "@/design-system/Tabs";
import { daysBetween } from "@/helpers/date";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import { PortfolioTicketDetailsModal } from "@/modules/portfolio/components/PortfolioTicketDetailsModal";
import { SessionFinishedModal } from "@/modules/portfolio/components/SessionFinishedModal";
import { getActiveTicketFilterCount } from "@/modules/tickets/pages/list/helpers";
import { useConfig } from "@/providers/config/useConfig";
import type { AdminTicketTabs, TicketSorting } from "@/queries/tickets/constants";
import {
  CLOSED_TICKET_TAB,
  IN_PROGRESS_TICKET_TAB,
  NEW_TICKET_TAB,
  REMINDER_TICKET_TAB,
} from "@/queries/tickets/constants";
import { routes } from "@/routes";
import type { TranslationFunction } from "@/translations";
import { useTranslation } from "@/translations";

import type { PortfolioTicketsFilterValues } from "./components/PortfolioTicketsFilterDrawer";
import { PortfolioTicketsFilterDrawer } from "./components/PortfolioTicketsFilterDrawer";

export interface PortfolioTicketListContentProps {
  tab: AdminTicketTabs;
  onChangeTab: (tab: AdminTicketTabs) => void;
  residentFocusMode: boolean;
  onUpdateResidentFocusMode: (val: boolean) => void;
  sorting: TicketSorting;
  onUpdateSorting: (val: TicketSorting) => void;
  search: string;
  onChangeSearch: (val: string) => void;
  filter: PortfolioTicketsFilterValues;
  onUpdateFilter: (val: PortfolioTicketsFilterValues) => void;
  totalTickets?: number;
  totalTicketsInTab?: number;
  tabsStats?: AdminTicketTabsStatsDto;
  tickets: PortfolioAdminTicketDto[];
  isLoadingTickets: boolean;
  hasMoreTickets: boolean | undefined;
  loadMoreTickets: () => void;
  isLoadingMoreTickets: boolean;
  deleteTicket: (ticket: { ticketId: string; projectId: string }) => void;
  markAsRead: () => void;
  openTicketModal: ModalState<{
    ticketId: string;
    projectId: string;
  }>;
  sessionFinishedModal: ModalState<{
    missedItems: number;
  }>;
  showNextTicketBtn: boolean;
  goToNextTicket: () => void;
  leftInSession?: number;
  /** Summary whose project scope and details-link access were validated by the page. */
  analyticsSummary: ReactNode;
}
export function PortfolioTicketListContent({
  filter,
  onUpdateFilter,
  tab,
  onChangeTab,
  search,
  onChangeSearch,
  totalTickets,
  totalTicketsInTab,
  tabsStats,
  tickets,
  isLoadingTickets,
  hasMoreTickets,
  loadMoreTickets,
  isLoadingMoreTickets,
  deleteTicket,
  markAsRead,
  residentFocusMode,
  onUpdateResidentFocusMode,
  sorting,
  onUpdateSorting,
  openTicketModal,
  sessionFinishedModal,
  showNextTicketBtn,
  goToNextTicket,
  leftInSession,
  analyticsSummary,
}: PortfolioTicketListContentProps): React.ReactNode {
  const { t } = useTranslation();
  const [isFilterMenuOpen, filterMenuHandler] = useBool(false);
  const [isMarkAsReadModalOpen, markAsReadModalHandler] = useBool(false);
  const ticketToDeleteModal = useModalState<PortfolioMinimalTicketDto>();
  const postHog = usePostHog();
  const showTicketPortfolioSettings = useConfig("showTicketPortfolioSettings");

  const tabs: TabItem<AdminTicketTabs>[] = [
    {
      value: NEW_TICKET_TAB,
      label: tabsStats == null ? t("page.tickets.table.tabs.new.loading") : t("page.tickets.table.tabs-label.new"),
      count: tabsStats?.newTickets,
    },
    {
      value: IN_PROGRESS_TICKET_TAB,
      label:
        tabsStats == null ? t("page.tickets.table.tabs.progress.loading") : t("page.tickets.table.tabs-label.progress"),
      count: tabsStats?.inProgressTickets,
    },
    {
      value: CLOSED_TICKET_TAB,
      label:
        tabsStats == null ? t("page.tickets.table.tabs.closed.loading") : t("page.tickets.table.tabs-label.closed"),
      count: tabsStats?.closedTickets,
    },
    {
      value: REMINDER_TICKET_TAB,
      label: tabsStats == null ? t("page.tickets.table.tabs.later.loading") : t("page.tickets.table.tabs-label.later"),
      icon: <ClockIcon />,
      isHidden: !tabsStats || tabsStats.reminderTickets === 0,
      count: tabsStats?.reminderTickets,
    },
  ];

  const amountActiveFilters = getActiveTicketFilterCount(filter);
  const isFilterActive = amountActiveFilters > 0 || !!search || residentFocusMode;

  function handleOpenTicket(ticketId: string, projectId: string) {
    postHog.capture("clicked_ticket");
    openTicketModal.open({
      ticketId,
      projectId,
    });
  }

  return (
    <>
      <DocumentPage
        containerBoundary="inherit"
        tabTitle={t("page.portfolio.tickets.title")}
        breadcrumbs={
          <Breadcrumbs
            pages={[
              {
                name: t("page.portfolio.title"),
              },
              {
                name: t("page.portfolio.tickets.title"),
              },
            ]}
          />
        }
        header={
          <section className="flex w-full flex-wrap items-center gap-space-sm @md:flex-nowrap">
            <div className="w-full max-w-measure-compact">
              <SearchInput
                data-testid="search-input"
                placeholder={t("page.tickets.search.placeholder")}
                value={search}
                onChange={(e) => onChangeSearch(e.target.value)}
              />
            </div>
            <Button
              variant="soft"
              icon={<FilterFunnel01Icon />}
              aria-pressed={isFilterMenuOpen}
              onClick={filterMenuHandler.toggle}
            >
              <Inline gap="sm" align="center" wrap={false} as="span">
                {t("common.action.filter")}
                {amountActiveFilters > 0 && <NumericBadge count={amountActiveFilters} variant="filled" />}
              </Inline>
            </Button>
          </section>
        }
        actions={
          <>
            <AskNobiButton
              defaultFilters={{ projectIds: filter.projectIds ?? [] }}
              context={{
                scope: "portfolio",
                entity: "tickets",
                page: "list",
                filters: {
                  owner: filter.owner,
                  maintenance: filter.maintenance,
                  statusKeys: filter.statusKeys,
                  categoryNames: filter.categoryNames,
                  assigneeAccountIds: filter.assigneeAccountIds,
                  assigneeMode: filter.assigneeMode,
                  projectIds: filter.projectIds,
                  ratings: filter.ratings,
                  isUnreadOnly: filter.isUnreadOnly,
                  visibility: filter.visibility,
                },
              }}
              suggestions={[
                t("component.ask-nobi-chat-drawer.portfolio-tickets.suggestion.1"),
                t("component.ask-nobi-chat-drawer.portfolio-tickets.suggestion.2"),
              ]}
            />
            {showTicketPortfolioSettings ? (
              <Button type="link" href={routes.portfolio.ticketSettings()}>
                {t("component.navigation.tickets.links.settings")}
              </Button>
            ) : null}
          </>
        }
      >
        <Stack gap="md">
          {analyticsSummary}
          <Tabs.Root<AdminTicketTabs> tabs={tabs} activeTab={tab} onSelectTab={onChangeTab}>
            <section className="flex w-full flex-col gap-space-sm">
              <TicketTableActions
                results={totalTickets}
                total={totalTicketsInTab}
                onUpdateFocus={onUpdateResidentFocusMode}
                sorting={sorting}
                onSort={onUpdateSorting}
                onMarkAsRead={markAsReadModalHandler.setTrue}
                isFilterActive={isFilterActive}
                tab={tab}
                isFocusModeOn={residentFocusMode}
                disableSorting={residentFocusMode && tab === IN_PROGRESS_TICKET_TAB}
              />
              {/* Desktop */}
              <section className="invisible hidden w-full @sm:visible @sm:block">
                <TicketsTable
                  handleOpenTicket={handleOpenTicket}
                  isFilterActive={isFilterActive}
                  tickets={tickets}
                  hasMoreTickets={hasMoreTickets}
                  loadMoreTickets={loadMoreTickets}
                  isLoadingTickets={isLoadingTickets}
                  isLoadingMoreTickets={isLoadingMoreTickets}
                  tab={tab}
                  onDelete={ticketToDeleteModal.open}
                />
              </section>
              {/* Mobile */}
              <section className="w-full @sm:invisible @sm:hidden">
                <TicketsMobileView
                  handleOpenTicket={handleOpenTicket}
                  isFilterActive={isFilterActive}
                  tickets={tickets}
                  hasMoreTickets={hasMoreTickets}
                  loadMoreTickets={loadMoreTickets}
                  isLoadingTickets={isLoadingTickets}
                  isLoadingMoreTickets={isLoadingMoreTickets}
                  tab={tab}
                  onDelete={ticketToDeleteModal.open}
                />
              </section>
            </section>
          </Tabs.Root>
        </Stack>
      </DocumentPage>

      <PortfolioTicketsFilterDrawer
        isOpened={isFilterMenuOpen}
        onOpenChange={filterMenuHandler.set}
        onUpdate={(newFilter) => onUpdateFilter(newFilter)}
        selectedFilter={{
          owner: filter.owner ?? [],
          maintenance: filter.maintenance ?? [],
          statusKeys: filter.statusKeys ?? [],
          categoryNames: filter.categoryNames ?? [],
          assigneeAccountIds: filter.assigneeAccountIds ?? [],
          assigneeMode: filter.assigneeMode,
          projectIds: filter.projectIds ?? [],
          ratings: filter.ratings ?? [],
          isUnreadOnly: filter.isUnreadOnly,
          visibility: filter.visibility,
        }}
      />
      <PortfolioTicketDetailsModal
        isOpened={openTicketModal.isOpen}
        onOpenChange={(state) => {
          if (!state) {
            openTicketModal.requestClose();
            openTicketModal.afterClose();
          }
        }}
        data={openTicketModal.data}
        leftInSession={leftInSession}
        showNextTicketBtn={showNextTicketBtn}
        onNavigateNextTicket={goToNextTicket}
      />
      <SessionFinishedModal
        isOpened={sessionFinishedModal.isOpen}
        onOpenChange={(state) => {
          if (!state) {
            sessionFinishedModal.requestClose();
            sessionFinishedModal.afterClose();
          }
        }}
      />
      <ConfirmModal
        title={t("page.tickets.delete.modal.title")}
        description={t("page.tickets.delete.modal.text")}
        isLoading={false}
        onOpenChange={(state) => {
          if (!state) {
            ticketToDeleteModal.requestClose();
          }
        }}
        tone="error"
        onReject={() => ticketToDeleteModal.requestClose()}
        rejectBtnProps={{
          "data-testid": "delete-ticket-modal-cancel",
        }}
        onResolve={() => {
          if (ticketToDeleteModal.data) {
            deleteTicket(ticketToDeleteModal.data);
          }
          ticketToDeleteModal.requestClose();
        }}
        resolveBtnProps={{
          "data-testid": "delete-ticket-modal-confirm",
          text: t("common.action.delete"),
        }}
        isOpened={ticketToDeleteModal.isOpen}
        shouldCloseOnEsc
        data-testid="delete-ticket-modal"
      />
      <ConfirmModal
        title={t("page.tickets.mark-all-as-read.modal.title")}
        description={t("page.tickets.mark-all-as-read.modal.text")}
        isLoading={false}
        tone="primary"
        onReject={markAsReadModalHandler.setFalse}
        onOpenChange={markAsReadModalHandler.set}
        rejectBtnProps={{
          "data-testid": "mark-all-as-read-ticket-modal-cancel",
        }}
        onResolve={() => {
          markAsRead();
          markAsReadModalHandler.setFalse();
          postHog.capture("clicked_maar_portfolio_tickets");
        }}
        resolveBtnProps={{
          "data-testid": "mark-all-as-read-ticket-modal-confirm",
          text: t("common.action.confirm"),
        }}
        isOpened={isMarkAsReadModalOpen}
        shouldCloseOnEsc
        data-testid="mark-all-as-read-ticket-modal"
      />
    </>
  );
}

interface TicketsTableProps {
  handleOpenTicket: (ticketId: string, projectId: string) => void;
  tickets: PortfolioAdminTicketDto[];
  hasMoreTickets: boolean | undefined;
  loadMoreTickets: () => void;
  isLoadingTickets: boolean;
  isLoadingMoreTickets: boolean;
  tab: AdminTicketTabs;
  isFilterActive: boolean;
  onDelete: (ticket: PortfolioMinimalTicketDto) => void;
}

function TicketsTable({
  handleOpenTicket,
  tickets,
  hasMoreTickets,
  loadMoreTickets,
  isLoadingTickets,
  isLoadingMoreTickets,
  isFilterActive,
  tab,
  onDelete,
}: TicketsTableProps): React.ReactNode {
  const clockDate = useCurrentDate();
  const { t, i18n } = useTranslation();
  const sessionUser = useSessionUser();

  const ref = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      if (!isLoadingMoreTickets && hasMoreTickets) {
        loadMoreTickets();
      }
    }, [isLoadingMoreTickets, hasMoreTickets, loadMoreTickets]),
  });

  const handleRowClick = useCallback(
    (row: DashboardRow<PortfolioAdminTicketDto>) => {
      handleOpenTicket(row.original.id, row.original.project.id);
    },
    [handleOpenTicket],
  );

  const columns = useMemo(() => {
    const helper = createDashboardColumnHelper<PortfolioAdminTicketDto>();

    return [
      helper.display({
        id: "visibility",
        cell: (cell) => {
          const value = cell.row.original.visibility;

          if (value === "private") {
            return null;
          }

          return <TicketVisibilityIndicator type={value} />;
        },
        size: TABLE_COLUMN_SIZES.VISUAL_INDICATOR.DEFAULT,
        enableResizing: false,
        meta: {
          isVisualIndicator: true,
        },
      }),
      helper.accessor("status", {
        header: t("page.tickets.table.header.status"),
        cell: (cell) => (
          <TicketStatus variant="filled" labelColor={cell.getValue().color} description={cell.getValue().name} />
        ),
        minSize: TABLE_COLUMN_SIZES.STATUS.MIN,
        size: TABLE_COLUMN_SIZES.STATUS.DEFAULT,
      }),
      helper.accessor("createdAt", {
        header: t("page.tickets.table.header.created-at"),
        cell: (cell) =>
          t("page.tickets.table.content.created-at", {
            count: daysBetween(cell.getValue(), clockDate),
          }),
        minSize: TABLE_COLUMN_SIZES.DATE.MIN,
        size: TABLE_COLUMN_SIZES.DATE.DEFAULT,
      }),
      helper.accessor("closedAt", {
        header: t("page.tickets.table.header.closed-at"),
        cell: (cell) =>
          t("page.tickets.table.content.closed-at", {
            when: formatDate(i18n, "dateMonthYearShort", cell.getValue()!),
            who: cell.row.original.closedBy?.fullName || cell.row.original.user.fullName,
          }),
        minSize: TABLE_COLUMN_SIZES.DATE.MIN,
        size: TABLE_COLUMN_SIZES.DATE.DEFAULT,
      }),
      helper.accessor("title", {
        header: t("page.tickets.table.header.title"),
        cell: (cell) => (
          <span data-testid="ticket-title-cell" className="flex w-full min-w-0 flex-col gap-space-xs">
            <span className="truncate text-left text-caption-emphasis">{cell.getValue()}</span>
            <Inline as="span" gap="xs" align="center" wrap={false}>
              {cell.row.original.remindAt && <ClockIcon size={12} tone="brand" />}
              <span className="truncate text-overline text-text-secondary">
                {tab === REMINDER_TICKET_TAB
                  ? formatDate(i18n, "datetimeShort", cell.row.original.remindAt!)
                  : t("page.tickets.table.content.title", {
                      when: formatDate(i18n, "datetimeShort", cell.row.original.lastActivityAt),
                      who: cell.row.original.lastActivityBy?.fullName || cell.row.original.user.fullName,
                    })}
              </span>
            </Inline>
          </span>
        ),
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("user", {
        header: t("page.tickets.table.header.reporter"),
        cell: (cell) => (
          <User
            user={cell.getValue()}
            size="sm"
            shouldNavigate={false}
            renderSubtitle={() => cell.row.original.locatedAt}
          />
        ),
        meta: {
          renderTruncatedTooltip: (cell) => {
            const data = cell.getValue();

            return <UserTooltipContent fullName={data.fullName} address={cell.row.original.locatedAt} />;
          },
        },
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("rating", {
        header: t("page.tickets.table.header.rating"),
        cell: (cell) => {
          const value = cell.getValue();

          if (value) {
            return <RatingBadge rating={value} />;
          }
        },
        minSize: TABLE_COLUMN_SIZES.STATUS.MIN,
        size: TABLE_COLUMN_SIZES.STATUS.DEFAULT,
      }),
      helper.accessor("activityCount", {
        header: t("page.tickets.table.header.activity"),
        cell: (cell) => {
          return (
            <Inline gap="xs" align="center" wrap={false}>
              <MessageCircle01Icon appearance={cell.row.original.hasUnreadActivity ? "highlighted" : undefined} />
              <span className="text-caption-emphasis">{cell.getValue()}</span>
            </Inline>
          );
        },
        minSize: TABLE_COLUMN_SIZES.COUNT.MIN,
        size: TABLE_COLUMN_SIZES.COUNT.DEFAULT,
      }),
      helper.accessor("assignee", {
        header: t("page.tickets.table.header.assignee"),
        cell: (cell) => {
          const data = cell.getValue();

          if (!data) {
            return (
              <Inline gap="sm" align="center" wrap={false}>
                <UserAvatar size="sm" image={undefined} />
                <span className="text-caption-emphasis">{t("page.tickets.table.content.unassigned")}</span>
              </Inline>
            );
          }

          return <User user={data} size="sm" shouldNavigate={false} />;
        },
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
        meta: {
          renderTruncatedTooltip: (cell) => {
            const data = cell.getValue();

            return <UserTooltipContent fullName={data ? data.fullName : t("page.tickets.table.content.unassigned")} />;
          },
        },
      }),
      helper.accessor("project.name", {
        header: t("page.tickets.table.header.project"),
        cell: ({ cell }) => <span>{cell.getValue()}</span>,
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
        meta: {
          renderTruncatedTooltip: (cell) => {
            const data = cell.getValue();

            return <span>{data}</span>;
          },
        },
      }),
      helper.accessor("integrations", {
        id: "rems",
        header: "",
        cell: ({ cell }) => {
          const data = getRemsStatus(cell.row.original);
          if (!data) {
            return null;
          }

          return <RemsStatus rems={data} />;
        },
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("id", {
        header: "",
        cell: ({ cell }) => {
          const menuActions = getTicketActions(t, cell.row.original, () =>
            onDelete({
              ticketId: cell.row.original.id,
              projectId: cell.row.original.project.id,
            }),
          );

          return <ContextMenu items={menuActions} />;
        },
        size: TABLE_COLUMN_SIZES.ACTION.DEFAULT,
        enableResizing: false,
        meta: {
          isAction: true,
        },
      }),
    ];
  }, [clockDate, t, i18n, tab, onDelete]);
  const tableState = useMemo(
    () =>
      ({
        columnVisibility: {
          status: tab !== NEW_TICKET_TAB || (tab === NEW_TICKET_TAB && sessionUser.isAdmin),
          closedAt: tab === CLOSED_TICKET_TAB,
          createdAt: tab !== CLOSED_TICKET_TAB,
          rating: tab === CLOSED_TICKET_TAB,
          assignee: tab !== CLOSED_TICKET_TAB,
        },
      }) satisfies Partial<DashboardTableState>,
    [sessionUser.isAdmin, tab],
  );
  const table = useResizableTable(TABLE_KEYS.PORTFOLIO_TICKETS, {
    columns: columns,
    data: tickets,
    state: tableState,
    enableSorting: false,
  });

  return (
    <section className="overflow-auto">
      {tickets.length === 0 && !isLoadingTickets && (
        <span className="block p-space-lg pb-space-xl text-caption-emphasis">
          {isFilterActive ? t("page.tickets.table.no-results") : t("page.tickets.table.empty")}
        </span>
      )}
      {(tickets.length > 0 || isLoadingTickets) && (
        <Table table={table} isLoading={isLoadingTickets && !isLoadingMoreTickets} onRowClick={handleRowClick} />
      )}
      {hasMoreTickets && (
        <footer className="h-illustration-compact p-space-md" ref={ref}>
          <Box padding={{ y: "md" }}>
            <LayoutInline justify="center">
              <Spinner size="md" />
            </LayoutInline>
          </Box>
        </footer>
      )}
    </section>
  );
}
function TicketsMobileView({
  handleOpenTicket,
  isFilterActive,
  hasMoreTickets,
  isLoadingMoreTickets,
  isLoadingTickets,
  loadMoreTickets,
  tab,
  tickets,
  onDelete,
}: TicketsTableProps) {
  const { t } = useTranslation();
  const ref = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      if (!isLoadingMoreTickets && hasMoreTickets) {
        loadMoreTickets();
      }
    }, [isLoadingMoreTickets, hasMoreTickets, loadMoreTickets]),
  });

  return (
    <section className="pb-space-sm">
      {isLoadingTickets ? (
        <Box padding="md">
          <Box padding={{ y: "md" }}>
            <LayoutInline justify="center">
              <Spinner size="md" />
            </LayoutInline>
          </Box>
        </Box>
      ) : (
        <>
          {tickets.length > 0 ? (
            <section className="flex flex-col gap-space-lg">
              {tickets.map((ticket) => {
                const currMenuActions = getTicketActions(t, ticket, () =>
                  onDelete({
                    ticketId: ticket.id,
                    projectId: ticket.project.id,
                  }),
                );

                return (
                  <TicketCard
                    key={ticket.id}
                    ticket={ticket}
                    onClick={() => handleOpenTicket(ticket.id, ticket.project.id)}
                    showRating={tab === CLOSED_TICKET_TAB}
                    menuActions={currMenuActions}
                    shouldNavigate={false}
                  />
                );
              })}
            </section>
          ) : (
            <span className="block p-space-lg pb-space-xl text-caption-emphasis">
              {isFilterActive ? t("page.tickets.table.no-results") : t("page.tickets.table.empty")}
            </span>
          )}
          {hasMoreTickets && (
            <footer className="h-illustration-compact p-space-md" ref={ref}>
              {(isLoadingTickets || isLoadingMoreTickets) && (
                <Box padding={{ y: "md" }}>
                  <LayoutInline justify="center">
                    <Spinner size="md" />
                  </LayoutInline>
                </Box>
              )}
            </footer>
          )}
        </>
      )}
    </section>
  );
}

function getTicketActions(
  t: TranslationFunction,
  ticket: PortfolioAdminTicketDto,
  onDelete: () => void,
): ContextMenuItem[] {
  const actions: ContextMenuItem[] = [
    {
      labels: {
        default: t("page.tickets.table.context-menu.delete"),
      },
      value: "delete",
      icon: <Trash02Icon />,
      callbackFn: onDelete,
      isHidden: !ticket.canDelete,
    },
  ];

  return actions;
}
